import type { CSSProperties } from 'react';

const norm = (v?: string | null) => (v || '').trim().toLowerCase();

interface ArticuloCatalogo { skuInterno: string; descripcion?: string | null }
interface ArticuloProveedor { skuInterno?: string | null; codigoProveedor?: string | null; activo?: boolean }

/** Resultados que muestra como mucho la lista del SKU de una fila de la OC. */
export const MAX_RESULTADOS_SKU = 15;

/**
 * Búsqueda en el catálogo desde el SKU de una fila de la OC: por SKU, descripción o código del proveedor de la OC.
 * Los índices se arman una vez por catálogo/artículos; cada búsqueda corta al llegar al límite. El SKU exacto va
 * primero (aunque en el catálogo esté más allá del límite): es el que elige Enter sin haber marcado otro.
 */
export function crearBuscadorCatalogo<T extends ArticuloCatalogo>(catalogo: T[], articulosProveedor: ArticuloProveedor[]) {
  const porSku = new Map<string, T>();
  for (const s of catalogo) porSku.set(norm(s.skuInterno), s);
  const codigosPorSku = new Map<string, string[]>();
  for (const a of articulosProveedor) {
    if (a.activo === false || !a.skuInterno || !a.codigoProveedor) continue;
    const sku = norm(a.skuInterno);
    codigosPorSku.set(sku, [...(codigosPorSku.get(sku) ?? []), norm(a.codigoProveedor)]);
  }
  return {
    esSku: (texto?: string) => !!norm(texto) && porSku.has(norm(texto)),
    buscar: (texto: string, limite = MAX_RESULTADOS_SKU): T[] => {
      const q = norm(texto);
      if (q.length < 2) return [];
      const exacto = porSku.get(q);
      const resultados: T[] = exacto ? [exacto] : [];
      for (const s of catalogo) {
        if (resultados.length >= limite) break;
        if (s === exacto) continue;
        const sku = norm(s.skuInterno);
        if (sku.includes(q) || norm(s.descripcion).includes(q) || (codigosPorSku.get(sku) ?? []).some(c => c.includes(q))) resultados.push(s);
      }
      return resultados;
    },
  };
}

const ALTO_MAX_DROPDOWN = 240;
const ANCHO_MIN_DROPDOWN = 360;
const SEPARACION = 2;
const MARGEN_PANTALLA = 8;

/**
 * Posición (fija) de la lista desplegable de un campo: debajo si entra, arriba si abajo no entra y arriba hay más
 * lugar; el alto se achica al espacio que queda y el ancho/izquierda no se salen de la pantalla.
 */
export function posicionDropdown(
  campo: { top: number; bottom: number; left: number; width: number },
  pantalla: { ancho: number; alto: number },
): CSSProperties {
  const width = Math.max(0, Math.min(Math.max(campo.width, ANCHO_MIN_DROPDOWN), pantalla.ancho - 2 * MARGEN_PANTALLA));
  const left = Math.max(MARGEN_PANTALLA, Math.min(campo.left, pantalla.ancho - width - MARGEN_PANTALLA));
  const base: CSSProperties = { position: 'fixed', left, width, zIndex: 9999 };
  const abajo = pantalla.alto - campo.bottom - SEPARACION - MARGEN_PANTALLA;
  const arriba = campo.top - SEPARACION - MARGEN_PANTALLA;
  if (abajo >= ALTO_MAX_DROPDOWN || abajo >= arriba) {
    return { ...base, top: campo.bottom + SEPARACION, maxHeight: Math.max(0, Math.min(ALTO_MAX_DROPDOWN, abajo)) };
  }
  return { ...base, bottom: pantalla.alto - campo.top + SEPARACION, maxHeight: Math.min(ALTO_MAX_DROPDOWN, arriba) };
}
